// ============================================================
// DockerIntro.jsx
// ============================================================
// Junior-dev Notes:
// - Added branding: prodevconsult.com
// - All typography set to TEXT_WHITE (#FFFFFF) for legibility.
// - FILE FIX: Image is correctly IMPORTED to resolve path.
// - CLEAN CODE: Removed stray characters and unused vars.
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardMedia,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import ComputerIcon from "@mui/icons-material/Computer";
import SpeedIcon from "@mui/icons-material/Speed";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LanguageIcon from "@mui/icons-material/Language";

/* ── Asset Imports ───────────────────────────────────────── */
import DockerInfrastructureMetaphor from "../../../assets/images/DockerInfrastructureMetaphor.jpg";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_GREEN = "#00ED64";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const GlassCard = styled(Paper)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    border: `1px solid rgba(13, 183, 237, 0.2)`,
    height: "100%",
}));

const CircuitCard = styled(Card)(({ theme }) => ({
    borderRadius: "16px",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    border: `1px solid rgba(13, 183, 237, 0.3)`,
    boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    height: "100%",
}));

export default function DockerIntro() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER WITH BRANDING */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Infrastructure · Module 01"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px", color: TEXT_WHITE }}>
                        What is <span style={{ color: DOCKER_BLUE }}>Docker?</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        "It works on my machine!" is a phrase of the past. Docker allows you to package
                        applications into containers that run identically on any system.
                    </Typography>
                </Box>

                {/* 2. THE CORE METAPHOR */}
                <Grid container spacing={4} sx={{ mb: 8 }} alignItems="stretch">
                    <Grid item xs={12} md={6}>
                        <GlassCard>
                            <Stack spacing={3}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <DirectionsBoatIcon sx={{ color: DOCKER_BLUE, fontSize: 40 }} />
                                    <Typography variant="h5" fontWeight={800} sx={{ color: TEXT_WHITE }}>The Shipping Metaphor</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                                    Before standard shipping containers, moving goods was chaotic. Every item
                                    needed a different box. Docker does for <strong>Software</strong> what the
                                    metal container did for <strong>Shipping</strong>.
                                </Typography>
                                <Typography variant="body1" sx={{ color: BRAND.muted, lineHeight: 1.8 }}>
                                    You put your code, libraries, and settings into one "container." Whether
                                    it's on a laptop, a test server, or the cloud—it just works.
                                </Typography>
                            </Stack>
                        </GlassCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <CircuitCard>
                            <CardMedia
                                component="img"
                                image={DockerInfrastructureMetaphor}
                                alt="Docker Container Concept Diagram"
                                sx={{ p: 4, objectFit: "contain", bgcolor: "rgba(255,255,255,0.02)" }}
                            />
                        </CircuitCard>
                    </Grid>
                </Grid>

                {/* 3. VM vs CONTAINER COMPARISON */}
                <Box sx={{ mb: 8, mt: 10 }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 4, textAlign: "center", color: TEXT_WHITE }}>
                        Why not just use a <span style={{ color: DOCKER_BLUE }}>Virtual Machine?</span>
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 4 }}>
                                <Typography variant="h6" color="error" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ComputerIcon /> Virtual Machines
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                                    Includes a full Guest OS (Gigabytes), slow to start, and heavy on resources.
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(13, 183, 237, 0.05)", border: `1px solid ${DOCKER_BLUE}`, borderRadius: 4 }}>
                                <Typography variant="h6" sx={{ color: DOCKER_BLUE, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SpeedIcon /> Docker Containers
                                </Typography>
                                <Typography variant="body2" sx={{ color: TEXT_WHITE, mb: 2 }}>
                                    Shared Host OS (Megabytes), starts in seconds, and incredibly lightweight.
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* 4. KEY BENEFITS */}
                <Box sx={{ mb: 10 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, color: TEXT_WHITE }}>Why Developers Love Docker</Typography>
                    <Grid container spacing={2}>
                        {[
                            { title: "Consistency", desc: "Same environment from Dev to Prod." },
                            { title: "Isolation", desc: "Apps don't interfere with each other." },
                            { title: "Scalability", desc: "Spin up 1 or 100 containers instantly." },
                            { title: "Efficiency", desc: "Lower hardware costs compared to VMs." }
                        ].map((benefit, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircleOutlineIcon sx={{ color: TERMINAL_GREEN, mt: 0.5, fontSize: 18 }} />
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEXT_WHITE }}>{benefit.title}</Typography>
                                        <Typography variant="caption" sx={{ color: BRAND.muted }}>{benefit.desc}</Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", mb: 6 }} />

                {/* 5. FOOTER & CTA */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 3, color: TEXT_WHITE }}>Ready to build your first image?</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 4 }}>
                        <Link to="/docker-architecture" style={{ textDecoration: "none" }}>
                            <Button
                                variant="contained"
                                size="large"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    bgcolor: DOCKER_BLUE,
                                    color: NAVY_BG,
                                    fontWeight: 800,
                                    px: 6,
                                    py: 2,
                                    borderRadius: "99px",
                                    "&:hover": { bgcolor: "#0091ca" }
                                }}
                            >
                                Next: Docker Architecture
                            </Button>
                        </Link>
                    </Stack>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ opacity: 0.5 }}>
                        <LanguageIcon sx={{ fontSize: 14 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Authorized Training by {BRAND.domain}
                        </Typography>
                    </Stack>
                </Box>

            </Container>
        </PageWrapper>
    );
}
